import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../lib/api";
import { Navbar } from "../components/Navbar";
import { VerifyBanner } from "../components/VerifyBanner";
import { Stars } from "../components/Ratings";
import { ReviewCard } from "../components/ReviewCard";
import { AccountDeletion } from "../components/AccountDeletion";
import { ShieldCheck, Lock, Plus, Send, UploadCloud, CheckCircle2, Clock, XCircle, ShieldAlert, CreditCard, CalendarClock, Receipt, Search } from "lucide-react";

const CLAIM_STATUS = {
  pending: { icon: Clock, label: "Awaiting admin approval", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { icon: CheckCircle2, label: "Approved", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { icon: XCircle, label: "Rejected", cls: "bg-red-50 text-red-700 border-red-200" },
};

export default function LandlordDashboard() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [props, setProps] = useState([]);
  const [claims, setClaims] = useState([]);
  const [sub, setSub] = useState({ price_gbp: 10, active_properties: 0, configured: true });
  const [claim, setClaim] = useState({ address_line: "", city: "", postcode: "", ownership_type: "owner" });
  const [proofFile, setProofFile] = useState(null);
  const [claimQuery, setClaimQuery] = useState("");
  const [claimResults, setClaimResults] = useState([]);
  const [selectedProp, setSelectedProp] = useState(null);
  const [manualMode, setManualMode] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [replyText, setReplyText] = useState({});
  const [disputeReason, setDisputeReason] = useState({});
  const [view, setView] = useState("properties");
  const [billing, setBilling] = useState([]);

  const load = () => {
    api.get("/landlord/properties").then((r) => setProps(r.data)).catch(() => {});
    api.get("/landlord/claims").then((r) => setClaims(r.data)).catch(() => {});
    api.get("/landlord/subscription").then((r) => setSub(r.data)).catch(() => {});
    api.get("/landlord/billing").then((r) => setBilling(r.data)).catch(() => {});
  };

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (user.role !== "landlord") { navigate("/"); return; }
    if (searchParams.get("subscribed")) {
      const slug = searchParams.get("subscribed");
      setNotice("Payment received — activating your subscription…");
      searchParams.delete("subscribed");
      setSearchParams(searchParams, { replace: true });
      // Fallback: verify the payment directly with Stripe in case the webhook is delayed/missing.
      const trySync = async (attempt = 0) => {
        try {
          const r = await api.post(`/properties/${slug}/sync-subscription`);
          if (r.data.active) {
            setNotice("✓ Your subscription is now active. Your response tools and Verified badge are unlocked.");
            load();
            return;
          }
        } catch (e) { /* ignore */ }
        if (attempt < 4) { setTimeout(() => trySync(attempt + 1), 2500); }
        else { setNotice("Payment received. If your tools aren't unlocked within a minute, please refresh — or contact support."); load(); }
      };
      trySync();
    }
    load();
  }, [user, loading, navigate]); // eslint-disable-line

  useEffect(() => {
    if (selectedProp || manualMode) return;
    const q = claimQuery.trim();
    if (q.length < 2) { setClaimResults([]); return; }
    const t = setTimeout(() => {
      api.get(`/properties/search?q=${encodeURIComponent(q)}`).then((r) => setClaimResults(r.data)).catch(() => setClaimResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [claimQuery, selectedProp, manualMode]);

  const doClaim = async (e) => {
    e.preventDefault();
    setError("");
    if (!selectedProp && !manualMode) { setError("Please search for and select your property, or add it manually."); return; }
    if (!proofFile) { setError("Please upload proof of ownership or management."); return; }
    setSubmitting(true);
    try {
      let slug;
      if (selectedProp) {
        slug = selectedProp.slug;
      } else {
        if (!claim.address_line || !claim.city || !claim.postcode) { setError("Please fill in the address, city and postcode."); setSubmitting(false); return; }
        const p = await api.post("/properties", { address_line: claim.address_line, city: claim.city, postcode: claim.postcode });
        slug = p.data.slug;
      }
      const fd = new FormData();
      fd.append("ownership_type", claim.ownership_type);
      fd.append("proof", proofFile);
      await api.post(`/properties/${slug}/claim`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setClaim({ address_line: "", city: "", postcode: "", ownership_type: "owner" });
      setProofFile(null); setSelectedProp(null); setManualMode(false); setClaimQuery(""); setClaimResults([]);
      setNotice("✓ Claim submitted! An admin will review your proof of ownership shortly — you'll be able to subscribe and respond once it's approved.");
      window.scrollTo({ top: 0, behavior: "smooth" });
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setSubmitting(false); }
  };

  const subscribeProperty = async (slug) => {
    setError("");
    try {
      const r = await api.post(`/properties/${slug}/subscribe`);
      if (r.data.payment_link) { window.location.href = r.data.payment_link; }
      else { load(); }
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || "Could not start checkout."); }
  };
  const cancelProperty = async (slug) => {
    if (!window.confirm("Cancel the £10/month subscription for this property? Your response tools and Verified badge for it will lock immediately.")) return;
    try { await api.post(`/properties/${slug}/unsubscribe`); load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };
  const sendReply = async (reviewId) => {
    const text = replyText[reviewId];
    if (!text?.trim()) return;
    try { await api.post(`/reviews/${reviewId}/reply`, { text }); setReplyText({ ...replyText, [reviewId]: "" }); load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };
  const uploadProof = async (reviewId, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("proof", file);
    try { await api.post(`/reviews/${reviewId}/proof`, fd, { headers: { "Content-Type": "multipart/form-data" } }); load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };
  const reverify = async (slug, file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("proof", file);
    try { await api.post(`/properties/${slug}/reverify`, fd, { headers: { "Content-Type": "multipart/form-data" } }); load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };
  const dispute = async (reviewId) => {
    const reason = disputeReason[reviewId];
    if (!reason?.trim()) { setError("Please give a reason for the dispute."); return; }
    if (!window.confirm("Raise a formal dispute on this review? The tenant has 14 days to confirm they stand by it, otherwise it is hidden.")) return;
    try { await api.post(`/reviews/${reviewId}/dispute`, { reason }); setDisputeReason({ ...disputeReason, [reviewId]: "" }); load(); }
    catch (err) { setError(formatApiError(err.response?.data?.detail)); }
  };

  const pendingClaims = claims.filter((c) => c.status !== "approved");

  return (
    <div className="App bg-[#F4F7FA] min-h-screen">
      <Navbar />
      <VerifyBanner />
      <div className="max-w-6xl mx-auto px-5 py-12">
        <span className="text-xs font-semibold uppercase tracking-[0.2em] font-mono text-mint-dark">Landlord portal</span>
        <h1 className="mt-1 font-heading font-extrabold text-3xl text-navy">My properties</h1>

        {notice && (
          <div className="mt-4 bg-emerald-50 border border-emerald-200 text-emerald-800 rounded-xl px-4 py-3 text-sm flex items-center justify-between gap-3" data-testid="subscribe-notice">
            <span>{notice}</span>
            <button onClick={load} className="font-semibold underline shrink-0">Refresh now</button>
          </div>
        )}

        {/* Membership summary */}
        <div className="mt-6 bg-navy text-white rounded-2xl p-5 flex items-center justify-between gap-4 flex-wrap" data-testid="membership-summary">
          <div className="flex items-center gap-3">
            <CreditCard className="w-6 h-6 text-mint shrink-0" />
            <div>
              <p className="font-heading font-bold">Per-property membership · £{sub.price_gbp}/month each</p>
              <p className="text-sm text-white/70">
                {sub.active_properties} active {sub.active_properties === 1 ? "subscription" : "subscriptions"} · claiming a property is free — subscribe per property to respond to reviews and earn its Verified Responsible Landlord badge.
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-xs font-mono uppercase tracking-wider text-white/50">Your monthly spend</p>
            <p className="font-heading font-extrabold text-3xl text-mint" data-testid="membership-spend">£{sub.active_properties * sub.price_gbp}</p>
          </div>
        </div>

        {!sub.configured && (
          <p className="mt-3 text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-xl px-4 py-2" data-testid="payments-not-configured">
            Online payments are not configured yet — please check back shortly.
          </p>
        )}

        {/* Tabs */}
        <div className="mt-6 flex gap-2 border-b border-slate-200" data-testid="landlord-tabs">
          {[{ k: "properties", label: "My properties" }, { k: "billing", label: "Subscriptions & billing" }].map((t) => (
            <button key={t.k} data-testid={`tab-${t.k}`} onClick={() => setView(t.k)}
              className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors ${view === t.k ? "border-mint text-navy" : "border-transparent text-slate-400 hover:text-slate-600"}`}>
              {t.label}
            </button>
          ))}
        </div>

        {view === "properties" && (<>
        {/* Claim form (always available) */}
        <form onSubmit={doClaim} className="mt-8 bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid="claim-form">
          <h2 className="font-heading font-bold text-lg text-navy flex items-center gap-2"><Plus className="w-5 h-5 text-mint-dark" /> Claim a property</h2>
          <p className="text-sm text-slate-500 mt-1">Search for your property below so your claim links to the existing reviewed listing (postcode spacing and minor differences are handled automatically). Upload proof of ownership (e.g. a mortgage statement or title) or, if you're a letting agent, proof of management. An admin reviews it before activation. Once approved you can subscribe (£{sub.price_gbp}/month) to respond.</p>

          {/* Step 1: find your property */}
          {!selectedProp && !manualMode && (
            <div className="mt-4">
              <div className="relative">
                <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input data-testid="claim-search" placeholder="Search your address or postcode (e.g. 12 High St or M14 5SZ)" value={claimQuery}
                  onChange={(e) => setClaimQuery(e.target.value)}
                  className="w-full border border-slate-300 rounded-xl pl-9 pr-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
              </div>
              {claimResults.length > 0 && (
                <div className="mt-2 border border-slate-200 rounded-xl divide-y divide-slate-100 overflow-hidden" data-testid="claim-results">
                  {claimResults.map((p) => (
                    <button type="button" key={p.slug} data-testid={`claim-result-${p.slug}`} onClick={() => { setSelectedProp(p); setClaimResults([]); }}
                      className="w-full text-left px-4 py-3 hover:bg-mint-soft transition-colors flex items-center justify-between gap-3">
                      <span className="text-sm font-semibold text-navy">{p.full_address}</span>
                      <span className="text-xs font-mono text-slate-400 shrink-0">{(p.aggregates && p.aggregates.count) || 0} reviews</span>
                    </button>
                  ))}
                </div>
              )}
              {claimQuery.trim().length >= 2 && claimResults.length === 0 && (
                <p className="mt-2 text-sm text-slate-500" data-testid="claim-no-match">
                  No existing listing matched. <button type="button" data-testid="claim-manual-toggle" onClick={() => setManualMode(true)} className="font-semibold text-mint-dark underline">Add this property manually</button>
                </p>
              )}
            </div>
          )}

          {/* Selected existing property */}
          {selectedProp && (
            <div className="mt-4 flex items-center justify-between gap-3 bg-mint-soft border border-mint rounded-xl px-4 py-3" data-testid="claim-selected">
              <div className="min-w-0">
                <p className="text-xs font-mono uppercase tracking-wider text-mint-dark">Claiming this property</p>
                <p className="font-semibold text-navy truncate">{selectedProp.full_address}</p>
              </div>
              <button type="button" data-testid="claim-clear" onClick={() => { setSelectedProp(null); setClaimQuery(""); }} className="text-sm font-semibold text-slate-500 hover:text-navy underline shrink-0">Change</button>
            </div>
          )}

          {/* Manual entry fallback */}
          {manualMode && (
            <div className="mt-4">
              <div className="grid sm:grid-cols-4 gap-3">
                <input data-testid="claim-address" placeholder="Street address" value={claim.address_line}
                  onChange={(e) => setClaim({ ...claim, address_line: e.target.value })}
                  className="border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint sm:col-span-2" />
                <input data-testid="claim-city" placeholder="City" value={claim.city}
                  onChange={(e) => setClaim({ ...claim, city: e.target.value })}
                  className="border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
                <input data-testid="claim-postcode" placeholder="Postcode" value={claim.postcode}
                  onChange={(e) => setClaim({ ...claim, postcode: e.target.value })}
                  className="border border-slate-300 rounded-xl px-3 py-2.5 outline-none focus:ring-2 focus:ring-mint" />
              </div>
              <button type="button" data-testid="claim-manual-back" onClick={() => { setManualMode(false); setClaim({ ...claim, address_line: "", city: "", postcode: "" }); }} className="mt-2 text-sm font-semibold text-slate-500 underline">← Back to search</button>
            </div>
          )}
          <div className="mt-3 grid sm:grid-cols-2 gap-3">
            {[{ v: "owner", t: "I own this property" }, { v: "letting_agent", t: "I'm the letting agent" }].map((o) => (
              <button key={o.v} type="button" data-testid={`ownership-${o.v}`} onClick={() => setClaim({ ...claim, ownership_type: o.v })}
                className={`p-3 rounded-xl border-2 text-sm font-semibold transition-colors ${claim.ownership_type === o.v ? "border-mint bg-mint-soft text-navy" : "border-slate-200 text-slate-600 hover:border-slate-300"}`}>
                {o.t}
              </button>
            ))}
          </div>
          <label htmlFor="proof-file" data-testid="claim-proof-zone"
            className="mt-3 flex items-center gap-3 border-2 border-dashed border-slate-300 rounded-xl p-4 cursor-pointer hover:border-mint transition-colors">
            <UploadCloud className="w-6 h-6 text-slate-400" />
            <span className="text-sm text-slate-600">{proofFile ? proofFile.name : "Upload proof of ownership / management (image or PDF)"}</span>
            <input id="proof-file" data-testid="claim-proof-input" type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => setProofFile(e.target.files[0])} />
          </label>
          <p className="mt-2 text-xs text-slate-500" data-testid="claim-retention-note">Retention &amp; re-verification: proof documents are used only to verify ownership and are permanently deleted once an admin decides on your claim. Verified ownership must be renewed every 12 months — you'll be prompted to upload fresh proof, with 14 days to do so before your tools are suspended.</p>
          {error && <p className="mt-3 text-sm text-destructive" data-testid="landlord-error">{error}</p>}
          <button data-testid="claim-submit" disabled={submitting} className="mt-4 bg-mint text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-mint-dark transition-colors disabled:opacity-60">
            {submitting ? "Submitting…" : "Submit claim"}
          </button>
        </form>

        {/* Pending claims */}
        {pendingClaims.length > 0 && (
          <div className="mt-8" data-testid="landlord-claims">
            <h2 className="font-heading font-bold text-xl text-navy">Claim status</h2>
            <div className="mt-3 flex flex-col gap-3">
              {pendingClaims.map((c) => {
                const s = CLAIM_STATUS[c.status];
                return (
                  <div key={c.id} className="bg-white border border-slate-200 rounded-2xl p-4 flex items-center justify-between gap-3 flex-wrap" data-testid={`claim-${c.id}`}>
                    <div>
                      <p className="font-semibold text-navy">{c.full_address}</p>
                      <p className="text-xs text-slate-500 font-mono">{c.ownership_type === "letting_agent" ? "Letting agent" : "Owner"}</p>
                    </div>
                    <span className={`flex items-center gap-1.5 px-3 py-1 text-xs font-semibold border rounded-md ${s.cls}`}>
                      <s.icon className="w-3.5 h-3.5" /> {s.label}{c.status === "rejected" && c.reject_reason ? `: ${c.reject_reason}` : ""}
                    </span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Approved properties */}
        <div className="mt-10 flex flex-col gap-8" data-testid="landlord-properties">
          {props.length === 0 && pendingClaims.length === 0 && <p className="text-slate-500">No claimed properties yet.</p>}
          {props.map((p) => {
            const active = p.subscription_active;
            return (
            <div key={p.id} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm" data-testid={`landlord-prop-${p.slug}`}>
              <div className="flex items-start justify-between gap-3 flex-wrap">
                <div>
                  <h3 className="font-heading font-bold text-xl text-navy">{p.full_address}</h3>
                  <div className="mt-1 flex items-center gap-2"><Stars value={p.aggregates.overall} /><span className="font-mono text-sm text-slate-500">{p.aggregates.overall.toFixed(1)} · {p.aggregates.count} reviews</span></div>
                </div>
                {active ? (
                  <div className="flex flex-col items-end gap-2">
                    <span className="bg-navy text-white px-3 py-1 text-sm font-semibold flex items-center gap-2 rounded-lg font-mono" data-testid={`sub-active-${p.slug}`}>
                      <ShieldCheck className="w-4 h-4 text-mint" /> Verified Responsible Landlord
                    </span>
                    <button data-testid={`unsubscribe-${p.slug}`} onClick={() => cancelProperty(p.slug)}
                      className="text-xs font-semibold text-slate-400 hover:text-red-600 underline transition-colors">
                      Cancel £{sub.price_gbp}/month subscription
                    </button>
                  </div>
                ) : (
                  <button data-testid={`subscribe-${p.slug}`} onClick={() => subscribeProperty(p.slug)} disabled={!sub.configured}
                    className="bg-mint text-white font-semibold px-4 py-2 rounded-xl hover:bg-mint-dark transition-colors text-sm disabled:opacity-60">
                    Subscribe £{sub.price_gbp}/month to respond
                  </button>
                )}
              </div>

              {p.reverification && (p.reverification.reverification_pending || p.reverification.due || p.reverification.suspended) && (
                <div data-testid={`reverify-banner-${p.slug}`}
                  className={`mt-4 rounded-xl border p-4 ${p.reverification.suspended ? "bg-red-50 border-red-200" : "bg-amber-50 border-amber-200"}`}>
                  {p.reverification.reverification_pending ? (
                    <p className="text-sm font-semibold text-amber-800 flex items-center gap-2" data-testid={`reverify-pending-${p.slug}`}>
                      <Clock className="w-4 h-4" /> Fresh proof of ownership uploaded — awaiting admin review. Your tools stay active in the meantime.
                    </p>
                  ) : (
                    <>
                      <p className={`text-sm font-semibold flex items-center gap-2 ${p.reverification.suspended ? "text-red-800" : "text-amber-800"}`}>
                        <ShieldAlert className="w-4 h-4" />
                        {p.reverification.suspended
                          ? "Ownership re-verification overdue — your reply, dispute and proof-of-work tools are suspended for this property."
                          : "Annual ownership re-verification due. Please upload fresh proof of ownership within 14 days to keep your tools active."}
                      </p>
                      <label htmlFor={`reverify-${p.slug}`} data-testid={`reverify-label-${p.slug}`}
                        className="mt-3 inline-flex items-center gap-2 text-sm font-semibold text-white bg-navy px-4 py-2 rounded-xl cursor-pointer hover:bg-navy-600 transition-colors w-fit">
                        <UploadCloud className="w-4 h-4" /> Upload fresh proof of ownership
                        <input id={`reverify-${p.slug}`} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => reverify(p.slug, e.target.files[0])} />
                      </label>
                      <p className="mt-2 text-xs text-slate-500">Retention note: uploaded ownership documents are stored only to verify your claim and are permanently deleted once an admin approves or rejects the re-verification.</p>
                    </>
                  )}
                </div>
              )}

              <div className="mt-6 flex flex-col gap-5">
                {p.reviews.length === 0 && <p className="text-slate-500 text-sm">No live reviews yet.</p>}
                {p.reviews.map((r) => (
                  <div key={r.id} className="relative">
                    <div className={active ? "" : "blur-sm pointer-events-none select-none"}>
                      <ReviewCard review={r} />
                    </div>
                    {!active ? (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white/40">
                        <Lock className="w-6 h-6 text-navy" />
                        <p className="mt-2 text-sm font-semibold text-navy">Subscribe to read & respond</p>
                      </div>
                    ) : (
                      <div className="mt-3 flex flex-col gap-3 border-l-4 border-mint pl-4">
                        {r.reply_pending ? (
                          <span className="text-sm text-amber-700 font-semibold flex items-center gap-1.5" data-testid={`reply-pending-${r.id}`}><Clock className="w-4 h-4" /> Your reply is awaiting admin approval — it will appear publicly once reviewed.</span>
                        ) : !r.landlord_reply && (
                          <div className="flex gap-2">
                            <input data-testid={`reply-input-${r.id}`} placeholder="Write a response…" value={replyText[r.id] || ""}
                              onChange={(e) => setReplyText({ ...replyText, [r.id]: e.target.value })}
                              className="flex-1 border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-mint text-sm" />
                            <button data-testid={`reply-send-${r.id}`} onClick={() => sendReply(r.id)}
                              className="bg-mint text-white px-4 rounded-xl hover:bg-mint-dark transition-colors flex items-center gap-1 text-sm font-semibold"><Send className="w-4 h-4" /> Reply</button>
                          </div>
                        )}
                        {r.resolved ? (
                          <span className="text-sm text-emerald-700 font-semibold flex items-center gap-1.5"><CheckCircle2 className="w-4 h-4" /> Proof of work uploaded · Resolved</span>
                        ) : r.proof_pending ? (
                          <span className="text-sm text-amber-700 font-semibold flex items-center gap-1.5" data-testid={`proof-pending-${r.id}`}><Clock className="w-4 h-4" /> Proof of work uploaded — awaiting admin approval before it shows publicly.</span>
                        ) : (
                          <label htmlFor={`proof-${r.id}`} data-testid={`proof-label-${r.id}`}
                            className="inline-flex items-start gap-2 text-sm font-semibold text-slate-700 cursor-pointer hover:text-mint-dark transition-colors w-fit max-w-lg">
                            <UploadCloud className="w-4 h-4 mt-0.5 shrink-0" /> <span>Upload proof of work (mark resolved) <span className="font-normal text-slate-500">— NOTE the proof of work / image will be shown LIVE on the site after review</span></span>
                            <input id={`proof-${r.id}`} type="file" accept="image/*,.pdf" className="hidden" onChange={(e) => uploadProof(r.id, e.target.files[0])} />
                          </label>
                        )}
                        {r.disputed ? (
                          <span className="text-sm text-amber-700 font-semibold flex items-center gap-1.5" data-testid={`disputed-note-${r.id}`}><ShieldAlert className="w-4 h-4" /> Dispute raised — awaiting tenant confirmation (14 days)</span>
                        ) : (
                          <div className="flex gap-2">
                            <input data-testid={`dispute-input-${r.id}`} placeholder="Disagree? Reason to dispute (UK notice & takedown)" value={disputeReason[r.id] || ""}
                              onChange={(e) => setDisputeReason({ ...disputeReason, [r.id]: e.target.value })}
                              className="flex-1 border border-slate-300 rounded-xl px-3 py-2 outline-none focus:ring-2 focus:ring-mint text-sm" />
                            <button data-testid={`dispute-btn-${r.id}`} onClick={() => dispute(r.id)}
                              className="border border-red-300 text-red-700 font-semibold px-4 rounded-xl hover:bg-red-50 transition-colors text-sm flex items-center gap-1"><ShieldAlert className="w-4 h-4" /> Dispute</button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
            );
          })}
        </div>
        </>)}

        {view === "billing" && (
          <div className="mt-8" data-testid="billing-panel">
            <h2 className="font-heading font-bold text-xl text-navy">Subscriptions &amp; billing</h2>
            <p className="text-sm text-slate-500 mt-1">Each active property is billed £{sub.price_gbp}/month via Stripe. See your next charge and download receipts below.</p>
            {billing.length === 0 ? (
              <p className="mt-6 text-slate-500" data-testid="billing-empty">No active subscriptions yet. Subscribe to a property from the “My properties” tab to start responding to reviews.</p>
            ) : (
              <div className="mt-4 bg-white border border-slate-200 rounded-2xl overflow-hidden">
                {billing.map((b) => (
                  <div key={b.slug} className="flex items-center justify-between gap-4 p-5 border-b border-slate-100 last:border-0 flex-wrap" data-testid={`billing-row-${b.slug}`}>
                    <div className="min-w-0">
                      <p className="font-semibold text-navy truncate">{b.full_address}</p>
                      <p className="text-xs text-slate-500 mt-1 flex items-center gap-2 flex-wrap">
                        <span className="flex items-center gap-1.5"><CalendarClock className="w-3.5 h-3.5" />
                          {b.next_billing_date ? `Next charge ${new Date(b.next_billing_date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : "Billing date syncing…"}
                        </span>
                        <span className="px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 font-mono" data-testid={`billing-status-${b.slug}`}>{b.status}</span>
                      </p>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="font-heading font-bold text-navy">£{b.amount_gbp}<span className="text-xs font-normal text-slate-400">/mo</span></span>
                      {b.receipt_url ? (
                        <a href={b.receipt_url} target="_blank" rel="noopener noreferrer" data-testid={`receipt-${b.slug}`}
                          className="flex items-center gap-1.5 border border-slate-300 text-slate-700 text-sm font-semibold px-3 py-1.5 rounded-lg hover:bg-slate-50 transition-colors">
                          <Receipt className="w-3.5 h-3.5" /> View receipt
                        </a>
                      ) : (
                        <span className="text-xs text-slate-400">Receipt pending</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        <AccountDeletion />
      </div>
    </div>
  );
}
